import { ChangeDetectionStrategy, Component, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { IconComponent } from '../../shared/icon/icon.component';
import { ThemeService } from '../../services/theme.service';
import { LoadingService } from '../../services/loading.service';
import { AuthService } from '../../services/auth.service';
import { TitleService } from '../../services/title.service';
import { SidebarService } from '../../services/sidebar.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-header',
  imports: [IconComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  public readonly themeService = inject(ThemeService);
  public readonly sidebarService = inject(SidebarService);
  public readonly loadingService = inject(LoadingService);
  public readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly titleService = inject(TitleService);

  public notificationsOpen = signal(false);
  public userMenuOpen = signal(false);

  // Get current user from auth service
  public readonly currentUser = computed(() => this.authService.currentUser());
  public readonly isAuthenticated = computed(() => this.authService.isAuthenticated());
  public readonly hasMultipleCompanies = computed(() => {
    const user = this.currentUser();
    return user && user.Companies && user.Companies.length > 1;
  });
  public readonly userInitials = computed(() => {
    const person = this.currentUser()?.Person;
    if (!person) return 'U';
    return ((person.firstName?.[0] ?? '') + (person.lastName?.[0] ?? '')).toUpperCase() || 'U';
  });

  public readonly companyName = computed(() => this.currentUser()?.Company?.name ?? '');

  public readonly userName = computed(() => {
    const person = this.currentUser()?.Person;
    if (!person) return '';
    return [person.firstName, person.infix, person.lastName].filter(Boolean).join(' ');
  });

  public readonly pageTitle = this.titleService.pageTitle;
  public readonly pageSubtitle = this.titleService.pageSubtitle;

  public readonly recentNotifications = this.notificationService.recentNotifications;
  public readonly unreadCount = this.notificationService.unreadCount;
  public readonly hasNotifications = computed(() => this.unreadCount() > 0);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Close user menu when clicking outside
    effect(() => {
      if (this.userMenuOpen()) {
        document.addEventListener('click', this.handleClickOutside);
      } else {
        document.removeEventListener('click', this.handleClickOutside);
      }
    });

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('click', this.handleClickOutside);
    });
  }

  private handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const userMenu = document.querySelector('.user-menu-wrapper');
    if (userMenu && !userMenu.contains(target)) {
      this.userMenuOpen.set(false);
    }
  };

  public onSearchSubmit(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    if (!value) return;

    if (/^\d+$/.test(value)) {
      this.router.navigate(['/zoeken'], { queryParams: { taxnumber: value, autoSearch: '1' } });
      input.value = '';
      return;
    }

    const stripped = value.replace(/-/g, '').toUpperCase();
    if (/^[A-Z0-9]+$/.test(stripped)) {
      this.router.navigate(['/zoeken'], { queryParams: { license: stripped, autoSearch: '1' } });
      input.value = '';
      return;
    }
  }

  public toggleNotifications(): void {
    this.notificationsOpen.update(v => !v);
    this.userMenuOpen.set(false);
  }

  public openNotificationCenter(): void {
    this.notificationsOpen.set(false);
    this.router.navigate(['/notificaties']);
  }

  public getRelativeTime(date: Date): string {
    return this.notificationService.getRelativeTime(date);
  }

  public getIconForType(type: string): string {
    switch (type) {
      case 'outbid': return 'alert';
      case 'ending': return 'timer';
      case 'new': return 'car';
      case 'won': return 'check';
      case 'lost': return 'x';
      default: return 'bell';
    }
  }

  public getIconClassForType(type: string): string {
    switch (type) {
      case 'outbid': return 'danger';
      case 'ending': return 'warning';
      case 'new': return 'primary';
      case 'won': return 'success';
      case 'lost': return 'neutral';
      default: return 'neutral';
    }
  }

  public toggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
    this.notificationsOpen.set(false);
  }

  public closeNotifications(): void {
    this.notificationsOpen.set(false);
  }

  public logout(): void {
    this.userMenuOpen.set(false);
    this.authService.logout();
  }

  public navigateToSettings(): void {
    this.userMenuOpen.set(false);
    this.router.navigate(['/instellingen']);
  }

  public switchCompany(): void {
    this.userMenuOpen.set(false);
    this.router.navigate(['/bedrijf-selecteren']);
  }
}
